import { OcrJobResult, OcrWordBox } from '../types';
import { PageTextCache } from '../locate/find-engine';

let workerPromise: Promise<any> | null = null;
let workerRef: any = null;
let progressHandler: ((p: number) => void) | null = null;

function stripIgnorable(s: string): string {
    return (s || '').replace(/[\u00AD\u200B-\u200D\uFEFF]/g, '');
}

export function buildPageCacheFromPlainText(pageIndex: number, plain: string): PageTextCache {
    const raw = stripIgnorable(plain);
    const text = raw.toLowerCase();
    return {
        pageIndex,
        text,
        items: raw
            ? [{ str: raw, start: 0, end: raw.length, sourceIndex: 0 }]
            : []
    };
}

export function buildPageCacheFromOcrWords(
    pageIndex: number,
    words: OcrWordBox[]
): { cache: PageTextCache; boxes: OcrWordBox[] } {
    const items: Array<{ str: string; start: number; end: number; sourceIndex: number }> = [];
    const boxes: OcrWordBox[] = [];
    let text = '';
    for (let i = 0; i < words.length; i++) {
        const w = words[i];
        const str = stripIgnorable(w.text || '');
        if (!str) {
            continue;
        }
        if (text.length > 0) {
            text += ' ';
        }
        const start = text.length;
        text += str;
        const sourceIndex = boxes.length;
        items.push({ str, start, end: text.length, sourceIndex });
        boxes.push({
            text: str,
            x0: w.x0,
            y0: w.y0,
            x1: w.x1,
            y1: w.y1
        });
    }
    return {
        cache: {
            pageIndex,
            text: text.toLowerCase(),
            items
        },
        boxes
    };
}

async function ensureWorker(): Promise<any> {
    if (workerRef) {
        return workerRef;
    }
    if (!workerPromise) {
        workerPromise = (async () => {
            const mod: any = await import('tesseract.js');
            const createWorker = mod.createWorker || (mod.default && mod.default.createWorker);
            const worker = await createWorker('eng', 1, {
                logger: (m: any) => {
                    if (!progressHandler || !m) {
                        return;
                    }
                    if (m.status === 'recognizing text' && typeof m.progress === 'number') {
                        progressHandler(m.progress);
                    }
                }
            });
            workerRef = worker;
            return worker;
        })();
    }
    return workerPromise;
}

export async function terminateTesseractWorker(): Promise<void> {
    progressHandler = null;
    const w = workerRef;
    workerRef = null;
    workerPromise = null;
    if (w) {
        try {
            await w.terminate();
        } catch { }
    }
}

async function loadImageElement(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('image load failed'));
        img.src = url;
    });
}

async function orientationProbeDataUrl(url: string, turns: number): Promise<string> {
    const img = await loadImageElement(url);
    const t = ((turns % 4) + 4) % 4;
    const srcW = img.naturalWidth || img.width;
    const srcH = img.naturalHeight || img.height;
    const swap = t % 2 === 1;
    const outW = swap ? srcH : srcW;
    const outH = swap ? srcW : srcH;
    const maxEdge = 800;
    const scale = Math.min(1, maxEdge / Math.max(outW, outH, 1));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(outW * scale));
    canvas.height = Math.max(1, Math.round(outH * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('2d context unavailable');
    }
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((t * Math.PI) / 2);
    ctx.scale(scale, scale);
    ctx.drawImage(img, -srcW / 2, -srcH / 2);
    return canvas.toDataURL('image/jpeg', 0.7);
}

function scoreOrientationText(text: string, confidence: number): number {
    const raw = (text || '').replace(/\s+/g, ' ').trim();
    if (!raw) {
        return 0;
    }
    const letters = (raw.match(/[A-Za-z]/g) || []).length;
    const digits = (raw.match(/\d/g) || []).length;
    const words = raw.split(' ').filter(w => /[A-Za-z]{3,}/.test(w)).length;
    const conf = Number.isFinite(confidence) ? confidence : 0;
    return conf * 2.2 + letters * 1.5 + digits * 1.2 + words * 4 + Math.min(raw.length, 500) * 0.12;
}

async function scoreOrientationTurns(imageSource: string, turns: number, worker: any): Promise<number> {
    const probe = await orientationProbeDataUrl(imageSource, turns);
    const result = await worker.recognize(probe);
    const data = result && result.data ? result.data : null;
    const text = data && data.text ? String(data.text) : '';
    const confidence = data && typeof data.confidence === 'number' ? data.confidence : 0;
    return scoreOrientationText(text, confidence);
}

export async function detectImageOrientationTurns(imageSource: string): Promise<number> {
    try {
        const worker = await ensureWorker();
        const zeroScore = await scoreOrientationTurns(imageSource, 0, worker);

        // Clean upright docs usually score well as-is — do not invent a rotation.
        if (zeroScore >= 120) {
            return 0;
        }

        let bestTurns = 0;
        let bestScore = zeroScore;
        for (let turns = 1; turns < 4; turns++) {
            const score = await scoreOrientationTurns(imageSource, turns, worker);
            if (score > bestScore) {
                bestScore = score;
                bestTurns = turns;
            }
        }

        if (bestTurns === 0) {
            return 0;
        }

        const improvement = bestScore - zeroScore;
        const ratio = zeroScore > 0 ? bestScore / zeroScore : (bestScore > 0 ? 99 : 1);
        if (improvement < 45 || ratio < 1.4) {
            return 0;
        }
        return bestTurns;
    } catch {
        return 0;
    }
}

export async function recognizeImageWithTesseract(
    imageSource: string,
    onProgress?: (p: number) => void
): Promise<OcrJobResult> {
    try {
        progressHandler = onProgress || null;
        const worker = await ensureWorker();
        if (onProgress) {
            onProgress(0);
        }
        const result = await worker.recognize(imageSource);
        const data = result && result.data ? result.data : null;
        const text = data && data.text ? String(data.text) : '';
        const rawWords = data && Array.isArray(data.words) ? data.words : [];
        const words: OcrWordBox[] = [];
        for (let i = 0; i < rawWords.length; i++) {
            const w = rawWords[i];
            if (!w || !w.bbox || typeof w.text !== 'string') {
                continue;
            }
            const t = stripIgnorable(w.text);
            if (!t) {
                continue;
            }
            words.push({
                text: t,
                x0: Number(w.bbox.x0) || 0,
                y0: Number(w.bbox.y0) || 0,
                x1: Number(w.bbox.x1) || 0,
                y1: Number(w.bbox.y1) || 0
            });
        }
        let imageWidth = 0;
        let imageHeight = 0;
        if (data) {
            if (data.imageSize) {
                imageWidth = Number(data.imageSize.width) || 0;
                imageHeight = Number(data.imageSize.height) || 0;
            }
            if ((!imageWidth || !imageHeight) && data.width && data.height) {
                imageWidth = Number(data.width) || 0;
                imageHeight = Number(data.height) || 0;
            }
        }
        if (onProgress) {
            onProgress(1);
        }
        progressHandler = null;
        if (!text.trim() && !words.length) {
            return {
                status: 'succeeded',
                message: 'No text found',
                text: '',
                words: [],
                imageWidth,
                imageHeight
            };
        }
        return {
            status: 'succeeded',
            message: 'Scan ready',
            text,
            words,
            imageWidth,
            imageHeight
        };
    } catch (err) {
        progressHandler = null;
        return {
            status: 'failed',
            message: (err && (err as Error).message) ? (err as Error).message : 'Scan failed'
        };
    }
}
